import { computed } from 'vue';
/**
 * 금액 검증 Composable
 *
 * 출금/이체/입금에서 공통으로 사용되는 금액 검증 로직을 처리합니다.
 * - 최소 금액 검증
 * - 잔고 초과 검증 (입금 제외)
 * - 잔고 부족 상태 확인
 *
 * @param config 검증 설정
 * @returns 검증 관련 computed 값들
 *
 * @example
 * // 출금 - 잔고 체크 O
 * const { isInsufficientBalance, error, isValid } = useAmountValidation({
 *   amount: computed(() => withdrawal.value.withdrawalAmount),
 *   minAmount: 10,
 *   availableAmount: computed(() => accountBalance.value),
 *   errorMessages: {
 *     min: '최소 출금 금액은 10$입니다',
 *     exceed: '출금 가능 금액을 초과했습니다',
 *   },
 * });
 *
 * @example
 * // 입금 - 잔고 체크 X
 * const { error, isValid } = useAmountValidation({
 *   amount: computed(() => deposit.value.depositAmount),
 *   minAmount: 10,
 *   errorMessages: {
 *     min: '최소 입금 금액은 10$입니다',
 *   },
 * });
 */
export const useAmountValidation = (config) => {
    /**
     * 잔고 부족 여부 체크
     * availableAmount가 있을 때만 체크 (입금은 체크 안함)
     */
    const isInsufficientBalance = computed(() => {
        if (!config.availableAmount)
            return false;
        return config.availableAmount.value === 0;
    });
    /**
     * 에러 검증
     * 입력된 금액의 유효성을 검사하고 에러 상태와 메시지 반환
     */
    const error = computed(() => {
        const amount = config.amount.value;
        // 금액이 0이거나 입력되지 않은 경우 에러 없음
        if (!amount || amount === 0) {
            return { hasError: false, message: '' };
        }
        // 잔고 초과 체크 (availableAmount가 있을 때만)
        if (config.availableAmount && amount > config.availableAmount.value) {
            return {
                hasError: true,
                message: config.errorMessages.exceed || '',
            };
        }
        // 최소 금액 체크
        if (amount < config.minAmount) {
            return {
                hasError: true,
                message: config.errorMessages.min,
            };
        }
        return { hasError: false, message: '' };
    });
    /**
     * 금액이 유효한지 여부
     * 모든 검증을 통과했을 때만 true
     */
    const isValid = computed(() => {
        const amount = config.amount.value;
        // 금액이 입력되지 않은 경우 유효하지 않음
        if (!amount || amount === 0) {
            return false;
        }
        // 잔고가 0원이면 유효하지 않음 (availableAmount가 있을 때만)
        if (isInsufficientBalance.value) {
            return false;
        }
        // 잔고 초과하면 유효하지 않음 (availableAmount가 있을 때만)
        if (config.availableAmount && amount > config.availableAmount.value) {
            return false;
        }
        // 최소 금액 미만이면 유효하지 않음
        if (amount < config.minAmount) {
            return false;
        }
        return true;
    });
    return {
        isInsufficientBalance,
        error,
        isValid,
    };
};
//# sourceMappingURL=useAmountValidation.js.map